import { useState } from "react";
import { useUser } from "../../auth/UserContext.js";
import { Icon } from "../../components/Icon.js";
import { Callout } from "../../components/Callout.js";
import { AllocRow } from "../../components/AllocRow.js";
import { fmt } from "../../lib/money.js";
import { patchAccount } from "../../api/account.js";
import type { KidBundle } from "../../api/me.js";

export default function Allowance() {
  const { bundle, refresh } = useUser();
  const [editOther, setEditOther] = useState(false);
  const [otherVal, setOtherVal] = useState("0");
  const [busy, setBusy] = useState(false);

  if (!bundle || bundle.user.role !== "kid") return null;
  const kid = bundle as KidBundle;

  const annualRate = kid.settings.interestRateAnnualBasisPoints / 10000;
  const monthlyRate = annualRate / 12;
  const activeBills = (kid.bills ?? []).filter((b) => b.monthsRemaining > 0);
  const totalBillsCents = activeBills.reduce((s, b) => s + b.amountCents, 0);
  const grossIncome = kid.account.allowanceCents + kid.account.otherIncomeCents;
  const income = grossIncome - totalBillsCents;

  const allocations = kid.goals.map((g) => {
    const amt = g.allocType === "$" ? g.allocValue : (income * g.allocValue) / 100;
    return { goal: g, amt };
  });
  const totalAllocated = allocations.reduce((s, a) => s + a.amt, 0);
  const free = income - totalAllocated;
  const maxAlloc = Math.max(...allocations.map((a) => a.amt), 1);
  const color = kid.user.color ?? "var(--brand)";

  const startEditOther = () => {
    setOtherVal((kid.account.otherIncomeCents / 100).toString());
    setEditOther(true);
  };

  const saveOther = async () => {
    const num = Number(otherVal);
    if (Number.isNaN(num)) return;
    setBusy(true);
    try {
      await patchAccount(kid.user.rowKey, { otherIncomeCents: Math.max(0, Math.round(num * 100)) });
      await refresh();
      setEditOther(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h1 style={{ marginBottom: 6 }}>Allowance &amp; allocation</h1>
      <p className="soft" style={{ marginBottom: 28 }}>
        Decide how you want to split up your monthly money.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 24 }}>
        {/* Income breakdown */}
        <div className="card">
          <div className="soft" style={{ fontSize: 13, marginBottom: 4 }}>
            Every month you get
          </div>
          <div className="money money-xl" style={{ marginBottom: 18 }}>
            {fmt(income)}
          </div>
          {totalBillsCents > 0 && (
            <div className="soft" style={{ fontSize: 12, marginBottom: 10, marginTop: -12 }}>
              {fmt(grossIncome)} gross − {fmt(totalBillsCents)} in bills
            </div>
          )}

          <div className="stack" style={{ gap: 10 }}>
            <div
              className="row-between"
              style={{ padding: "10px 0", borderBottom: "1px solid var(--hairline-soft)" }}
            >
              <div className="row">
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: "var(--brand-soft)",
                    color: "var(--brand)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon name="piggy" size={16} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>Allowance</div>
                  <div className="soft" style={{ fontSize: 12 }}>
                    1st of every month
                  </div>
                </div>
              </div>
              <div className="money money-sm">{fmt(kid.account.allowanceCents)}</div>
            </div>
            <div
              className="row-between"
              style={{ padding: "10px 0", borderBottom: "1px solid var(--hairline-soft)" }}
            >
              <div className="row">
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: "var(--canvas-warm)",
                    color: "var(--ink-soft)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon name="check" size={16} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>Other income</div>
                  <div className="soft" style={{ fontSize: 12 }}>
                    Chores, gifts — your estimate
                  </div>
                </div>
              </div>
              {!editOther ? (
                <div className="row" style={{ gap: 8 }}>
                  <div className="money money-sm">{fmt(kid.account.otherIncomeCents)}</div>
                  <button className="btn btn-ghost btn-sm" onClick={startEditOther}>
                    Edit
                  </button>
                </div>
              ) : (
                <div className="row" style={{ gap: 6 }}>
                  <div style={{ fontSize: 16 }}>$</div>
                  <input
                    type="number"
                    value={otherVal}
                    onChange={(e) => setOtherVal(e.target.value)}
                    style={{
                      width: 70,
                      padding: "6px 8px",
                      border: "1px solid var(--hairline)",
                      borderRadius: 8,
                      textAlign: "right",
                      fontSize: 14,
                    }}
                    autoFocus
                  />
                  <button className="btn btn-brand btn-sm" onClick={saveOther} disabled={busy}>
                    Save
                  </button>
                </div>
              )}
            </div>
            {activeBills.map((bill) => (
              <div
                key={bill.rowKey}
                className="row-between"
                style={{ padding: "10px 0", borderBottom: "1px solid var(--hairline-soft)" }}
              >
                <div className="row">
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: "oklch(0.95 0.03 27)",
                      color: "var(--neg)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon name="minus" size={16} />
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{bill.description}</div>
                    <div className="soft" style={{ fontSize: 12 }}>
                      {bill.monthsRemaining} month{bill.monthsRemaining !== 1 ? "s" : ""} remaining
                    </div>
                  </div>
                </div>
                <div className="money money-sm money-neg">−{fmt(bill.amountCents)}</div>
              </div>
            ))}
            <div className="row-between" style={{ padding: "10px 0" }}>
              <div className="row">
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: "oklch(0.94 0.03 195)",
                    color: "var(--brand)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon name="sparkle" size={16} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>Bank interest</div>
                  <div className="soft" style={{ fontSize: 12 }}>
                    ≈ {(annualRate * 100).toFixed(1)}% per year on balance
                  </div>
                </div>
              </div>
              <div className="money money-sm">
                +{fmt(Math.round(kid.account.balanceCents * monthlyRate))}
              </div>
            </div>
          </div>
        </div>

        {/* Allocation visual */}
        <div className="card">
          <div className="row-between" style={{ marginBottom: 14 }}>
            <h3>Where does it go?</h3>
            <div className="chip">
              {fmt(totalAllocated)} of {fmt(income)} allocated
            </div>
          </div>

          {/* Stacked bar */}
          <div
            style={{
              display: "flex",
              height: 40,
              borderRadius: 12,
              overflow: "hidden",
              marginBottom: 20,
              border: "1px solid var(--hairline-soft)",
            }}
          >
            {allocations.map(({ goal, amt }) => {
              const pct = income > 0 ? (amt / income) * 100 : 0;
              return (
                <div
                  key={goal.rowKey}
                  title={`${goal.title}: ${fmt(amt)}`}
                  style={{
                    width: `${pct}%`,
                    background: color,
                    opacity: 0.3 + 0.7 * (amt / maxAlloc),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: 11,
                    fontWeight: 500,
                  }}
                >
                  {pct > 8 && goal.title.split(" ")[0]}
                </div>
              );
            })}
            {free > 0 && income > 0 && (
              <div
                style={{
                  width: `${(free / income) * 100}%`,
                  background: "var(--canvas-warm)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--ink-soft)",
                  fontSize: 11,
                  fontWeight: 500,
                }}
              >
                {(free / income) * 100 > 8 ? "Free" : ""}
              </div>
            )}
          </div>

          {/* Per-goal sliders */}
          <div className="stack" style={{ gap: 14 }}>
            {kid.goals.map((g) => (
              <AllocRow
                key={g.rowKey}
                goal={g}
                kid={kid.user}
                income={income}
                kidId={kid.user.rowKey}
                onUpdated={refresh}
              />
            ))}
          </div>

          <div style={{ marginTop: 18 }}>
            <Callout>
              <div style={{ fontSize: 13 }}>
                {free >= 0 ? (
                  <>
                    You have <b>{fmt(free)}</b> left over each month to spend freely. The rest goes
                    straight toward your goals!
                  </>
                ) : (
                  <>
                    You&apos;ve allocated <b>{fmt(-free)}</b> more than you earn. Lower one of your
                    goals or save less per month.
                  </>
                )}
              </div>
            </Callout>
          </div>
        </div>
      </div>
    </div>
  );
}
